import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from '@/components/settings/types';
import { useAuth } from '@/lib/auth/useAuth';
import { OnboardingModal } from '@/components/onboarding/OnboardingModal';
import { useLocation } from 'react-router-dom';
import { identifyUser, trackEvent, resetUser } from '@/lib/analytics';
import { identifyUser as koalaIdentifyUser, trackEvent as koalaTrackEvent } from '@/lib/koala';
import { isEvalOpsStaff } from '@/lib/utils/user';
import { getUserDetails, updateUserDetails, UserDetails } from '@/api/users';

interface UserContextType {
  user: User | null;
  isLoading: boolean;
  error: Error | null;
  isAuthenticated: boolean;
  needsOnboarding: boolean;
  setNeedsOnboarding: (needsOnboarding: boolean) => void;
  updateUser: (userData: Partial<User>) => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

// Helper function to get cached user data
const getCachedUser = (): User | null => {
  try {
    const cached = localStorage.getItem('cached_user');
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

// Helper function to set cached user data
const setCachedUser = (user: User | null) => {
  if (user) {
    if (import.meta.env.DEV) console.log('[User] Caching user data with onboarding_completed:', user.onboarding_completed);
    localStorage.setItem('cached_user', JSON.stringify(user));
  } else {
    localStorage.removeItem('cached_user');
  }
};

export function UserProvider({ children }: { children: React.ReactNode }) {
  const { user: authUser, isAuthenticated, loading: authLoading } = useAuth();
  
  // Extract primitive values to avoid object reference dependencies
  const authUserId = authUser?.id;
  const authUserEmail = authUser?.email;
  const [user, setUser] = useState<User | null>(getCachedUser());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(() => {
    const cachedUser = getCachedUser();
    return cachedUser ? !cachedUser.onboarding_completed : false;
  });
  const location = useLocation();
  const isOnboardingPage = location.pathname === '/onboarding';

  useEffect(() => {
    const syncUser = async () => {
      try {
        if (!authLoading && authUser && isAuthenticated) {
          const cachedUser = getCachedUser();
          if (cachedUser && cachedUser.id === authUserId) {
            setUser(cachedUser);
            setNeedsOnboarding(!cachedUser.onboarding_completed);
            // Identify user in PostHog with cached data
            identifyUser(cachedUser.id, {
              email: cachedUser.email,
              name: cachedUser.name,
              role: cachedUser.role,
              onboarding_completed: cachedUser.onboarding_completed,
              is_staff: cachedUser.metadata?.is_staff
            });
            
            // Identify user in Koala with cached data
            koalaIdentifyUser(cachedUser.email, {
              name: cachedUser.name,
              role: cachedUser.role,
              onboardingCompleted: cachedUser.onboarding_completed,
              isStaff: cachedUser.metadata?.is_staff === 'true',
              lastActive: cachedUser.lastActive
            });
            setIsLoading(false);
            return;
          }
          try {
            if (!authUserId) {
              throw new Error('No user ID available');
            }
            const result = await getUserDetails(authUserId);
            if (result.success && result.data) {
              const userData = result.data;
              const mappedUser: User = {
                id: authUserId,
                name: userData.name || '',
                email: authUserEmail || '',
                role: (userData.role as 'member' | 'admin' | 'auditor') || 'member',
                lastActive: new Date().toISOString(),
                metadata: {
                  is_staff: isEvalOpsStaff(authUserEmail || '').toString()
                },
                onboarding_completed: userData.onboarding_completed > 0,
                notification_settings: userData.notification_settings || {
                  email: true,
                  slack: false,
                  webhook: false,
                  test_failures: true,
                  test_successes: false,
                  alert_triggers: true,
                  system_updates: true
                }
              };
              setUser(mappedUser);
              setCachedUser(mappedUser);
              setNeedsOnboarding(!mappedUser.onboarding_completed);
              
              // DEBUG: Log onboarding state
              if (import.meta.env.DEV) {
                console.log('[UserContext] User loaded:', {
                  id: mappedUser.id,
                  onboarding_completed: mappedUser.onboarding_completed,
                  needsOnboarding: !mappedUser.onboarding_completed,
                  raw_onboarding_value: userData.onboarding_completed
                });
              }
              // Identify user in PostHog with fresh data
              identifyUser(mappedUser.id, {
                email: mappedUser.email,
                name: mappedUser.name,
                role: mappedUser.role,
                onboarding_completed: mappedUser.onboarding_completed,
                is_staff: mappedUser.metadata?.is_staff
              });
              
              // Identify user in Koala with fresh data
              koalaIdentifyUser(mappedUser.email, {
                name: mappedUser.name,
                role: mappedUser.role,
                onboardingCompleted: mappedUser.onboarding_completed,
                isStaff: mappedUser.metadata?.is_staff === 'true',
                lastActive: mappedUser.lastActive
              });
              if (userData.onboarding_completed !== undefined) {
                setNeedsOnboarding(!userData.onboarding_completed);
              }
              return;
            }
          } catch (err) {
            // fallback
          }
          const fallbackName = `${authUser?.firstName || ''} ${authUser?.lastName || ''}`.trim() || (authUserEmail || '').split('@')[0];
          const mappedUser: User = {
            id: authUserId || '',
            name: fallbackName,
            email: authUserEmail || '',
            role: 'member',
            lastActive: new Date().toISOString(),
            metadata: {
              is_staff: isEvalOpsStaff(authUserEmail || '').toString()
            },
            onboarding_completed: false,
            notification_settings: {
              email: true,
              slack: false,
              webhook: false,
              test_failures: true,
              test_successes: false,
              alert_triggers: true,
              system_updates: true
            }
          };
          setUser(mappedUser);
          setCachedUser(mappedUser);
          // Identify user in PostHog with fallback data
          identifyUser(mappedUser.id, {
            email: mappedUser.email,
            name: mappedUser.name,
            role: mappedUser.role,
            onboarding_completed: mappedUser.onboarding_completed,
            is_staff: mappedUser.metadata?.is_staff
          });
          
          // Identify user in Koala with fallback data
          koalaIdentifyUser(mappedUser.email, {
            name: mappedUser.name,
            role: mappedUser.role,
            onboardingCompleted: mappedUser.onboarding_completed,
            isStaff: mappedUser.metadata?.is_staff === 'true',
            lastActive: mappedUser.lastActive
          });
          setNeedsOnboarding(true);
        } else if (!authLoading && !isAuthenticated) {
          setUser(null);
          setCachedUser(null);
          setNeedsOnboarding(false);
          // Reset PostHog context on logout
          resetUser();
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to sync user'));
      } finally {
        setIsLoading(false);
      }
    };
    syncUser();
  }, [authUserId, authUserEmail, isAuthenticated, authLoading]);

  const updateUser = async (userData: Partial<User>) => {
    try {
      setIsLoading(true);
      if (!user?.id) {
        throw new Error('No user ID available for update');
      }
      // Only make API call if we're updating the user profile
      if (
        userData.name ||
        userData.email ||
        userData.role ||
        userData.onboarding_completed !== undefined ||
        userData.notification_settings
      ) {
        const result = await updateUserDetails(user.id, {
          name: userData.name,
          email: userData.email,
          role: userData.role,
          notification_settings: userData.notification_settings,
          onboarding_completed: userData.onboarding_completed ? 1 : 0
        });
        if (!result.success) {
          throw new Error(result.error || 'Failed to update user');
        }
        localStorage.removeItem('onboarding_completed');
      }
      setUser(prev => {
        if (!prev) return null;
        const updatedUser = { ...prev, ...userData };
        setCachedUser(updatedUser);
        if (userData.onboarding_completed) {
          setNeedsOnboarding(false);
          trackEvent('onboarding_completed', {
            userId: updatedUser.id,
            name: updatedUser.name,
            email: updatedUser.email,
            role: updatedUser.role
          });
          identifyUser(updatedUser.id, {
            email: updatedUser.email,
            name: updatedUser.name,
            role: updatedUser.role,
            onboarding_completed: true,
            is_staff: updatedUser.metadata?.is_staff
          });
          
          // Track onboarding completion in Koala
          koalaTrackEvent('onboarding_completed', {
            userId: updatedUser.id,
            email: updatedUser.email,
            name: updatedUser.name,
            role: updatedUser.role
          });
          koalaIdentifyUser(updatedUser.email, {
            name: updatedUser.name,
            role: updatedUser.role,
            onboardingCompleted: true,
            isStaff: updatedUser.metadata?.is_staff === 'true',
            lastActive: updatedUser.lastActive
          });
        }
        return updatedUser;
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update user'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <UserContext.Provider 
      value={{ 
        user, 
        isLoading: isLoading || authLoading, 
        error, 
        isAuthenticated,
        needsOnboarding,
        setNeedsOnboarding,
        updateUser
      }}
    >
      {children}
      {needsOnboarding && !isOnboardingPage && !isLoading && user && (
        <OnboardingModal />
      )}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
} 